import {
  Box,
  InputAdornment,
  ListItem,
  ListItemText,
  MenuList,
  TextField,
  Typography,
} from "@mui/material";
import { memo, useState } from "react";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import useTranslate from "hooks/useTranslate";
import { useThemeColors } from "../../theme/hooks/useThemeColors";

export interface IFilterListBoxItem {
  id: string;
  name: string;
}

export interface IFilterListBoxProps {
  data: any;
  onSelectedItemChanged: (item: IFilterListBoxItem | null) => void;
}

function FilterListBox(props: IFilterListBoxProps) {
  const colors = useThemeColors();
  const [selectedItem, setSelectedItem] = useState<IFilterListBoxItem | null>();
  const [searchTerm, setSearchTerm] = useState("");
  const { translate } = useTranslate("pos.userHome");
  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
    setSelectedItem(null);
    props.onSelectedItemChanged(null);
  };
  
  const filteredItems =
    props.data &&
    props.data.filter((item: IFilterListBoxItem) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  return (
    <Box
      sx={{
        border: `1px solid ${colors.border.primary}`,
        borderRadius: "4px",
      }}
    >
      <Box
        sx={{
          borderRadius: "4px 4px 0 0",
          borderBottom: `1px solid ${colors.border.secondary}`,
          background: colors.background.tertiary,
          padding: 1.5,
        }}
      >
        <TextField
          size="small"
          sx={{
            background: colors.components.input.background,
          }}
          placeholder={translate("searchForPos")}
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={handleSearchChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <PointOfSaleOutlinedIcon
                    sx={{
                      color: colors.components.icon.color,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon
                    sx={{
                      color: colors.components.icon.color,
                    }}
                  />
                </InputAdornment>
              ),
            }
          }}
        />
      </Box>
      <Box
        sx={{
          maxHeight: 250,
          minHeight: 250,
          overflow: "auto",
          background: colors.background.primary,
          padding: 0,
          "&::-webkit-scrollbar": {
            width: "12px",
          },
          "&::-webkit-scrollbar-track": {
            background: colors.background.primary,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.background.tertiary,
            "&:hover": {
              background: colors.background.hover,
            },
          },
        }}
      >
        <MenuList
          style={{
            background: colors.background.primary,
          }}
        >
          {filteredItems &&
            filteredItems.map((item: IFilterListBoxItem) => (
              <ListItem
                data-testid="user-home-pos-item"
                sx={{
                  backgroundColor:
                    selectedItem === item
                      ? colors.brand.secondary
                      : "inherit",
                  "&:hover": {
                    backgroundColor:
                      selectedItem === item
                        ? colors.background.hover
                        : colors.background.hover,
                  },
                }}
                onClick={() => {
                  setSelectedItem(item);
                  props.onSelectedItemChanged(item);
                }}
                key={item.id}
              >
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        color:
                          selectedItem === item
                            ? colors.brand.primary
                            : colors.text.secondary,
                        fontSize: "16px",
                        fontStyle: "normal",
                        fontWeight: "300",
                        lineHeight: "150%",
                        letterSpacing: "0.15px",
                      }}
                    >
                      {item.name}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
        </MenuList>
      </Box>
    </Box>
  );
}
export default memo(FilterListBox);
